/**
 * Booking Context
 * 
 * React Context for managing booking flow state (date, time, bay selections).
 * 
 * **Purpose**: Store user selections that need to be shared across booking
 * flow components and persisted during the session.
 * 
 * **Scope**: Should only be mounted on booking-related routes.
 * 
 * **NOT for**:
 * - Server data (club, user, etc.) - use React Query hooks
 * - URL params (clubId, etc.) - use appRuntime service
 * 
 * @module booking/context
 */

import { 
  createContext, 
  useState, 
  useEffect, 
  useCallback, 
  type ReactNode 
} from 'react';
import type { BookingContextValue, PaymentProvider } from '../types';
import { extractLocalDateISO, asDateString, isValidSlotTime, type DateString } from '@butter/library/datetime';
import type { User } from '~/services/auth/types';
import type { DurationBayAvailabilityPayload, WaiverSignaturePaths } from '@lgc/database/types';
import { 
  saveToSession, 
  loadFromSession, 
  clearFromSession,
  clearAllBookingData 
} from './sessionStorage';

/**
 * Booking Context
 * 
 * Provides booking state and actions to all child components.
 */
export const BookingContext = createContext<BookingContextValue | null>(null);



/**
 * Props for BookingProvider
 */
interface BookingProviderProps {
  children: ReactNode;
  /**
   * Persist selections to sessionStorage (default). The demo flow mounts
   * `persist={false}`: its fixed-duration selections must not leak into the
   * standard flow's restored state (or vice versa), and it has no payment
   * redirect that would justify surviving a reload.
   */
  persist?: boolean;
}

/**
 * Booking Provider
 * 
 * Manages booking selections with automatic sessionStorage persistence.
 * 
 * **Important**: This provider should only wrap booking-related routes,
 * not the entire app. Mount it in the booking routes configuration.
 * 
 * @example
 * ```tsx
 * // In routes/booking.routes.tsx
 * {
 *   path: '/',
 *   element: (
 *     <BookingProvider>
 *       <BookingFlowPage />
 *     </BookingProvider>
 *   ),
 * }
 * ```
 */
export function BookingProvider({ children, persist = true }: BookingProviderProps) {
  // Initialize booking selection state from storage or defaults
  const [selectedDate, setSelectedDateState] = useState<DateString>(extractLocalDateISO(new Date()));
  const [selectedStartTime, setSelectedStartTimeState] = useState<string | null>(null);
  const [selectedEndTime, setSelectedEndTimeState] = useState<string | null>(null);
  const [selectedBayId, setSelectedBayIdState] = useState<number | null>(null);
  const [guestCount, setGuestCountState] = useState<number>(0);
  
  // Initialize payment & user state (NOT persisted to storage for security)
  const [guestUser, setGuestUserState] = useState<User | null>(null);
  const [isExistingUser, setIsExistingUserState] = useState<boolean>(false);
  const [paymentMethodToken, setPaymentMethodTokenState] = useState<string | null>(null);
  const [paymentProvider, setPaymentProviderState] = useState<PaymentProvider | null>(null);
  const [payAtClub, setPayAtClubState] = useState<boolean>(false);

  // Waiver state — deliberately NOT persisted, for the same reason
  // `paymentMethodToken` isn't: a drawn signature is a legal artifact, and
  // sessionStorage is the wrong place for one.
  const [waiverSignatures, setWaiverSignaturesState] = useState<Record<number, WaiverSignaturePaths | null>>({});
  const [waiverAgreements, setWaiverAgreementsState] = useState<Record<number, boolean>>({});

  // Initialize available bays state
  const [availableBays, setAvailableBaysState] = useState<DurationBayAvailabilityPayload | null>(null);
  
  // `persist === false` has nothing to restore, so the provider is open for
  // writes at once — seeding from the prop (rather than a setState in the
  // load effect) is what keeps the react-hooks/set-state-in-effect rule green.
  const [isInitialized, setIsInitialized] = useState(!persist);
  
  // Load initial state from storage on mount
  useEffect(() => {
    if (!persist) return; // nothing to restore; isInitialized seeded true above
    async function loadInitialState() {
      try {
        const [storedDate, storedStartTime, storedEndTime, storedBayId, storedGuestUser, storedIsExistingUser, storedGuestCount] = await Promise.all([
          loadFromSession<string>('selectedDate'),
          loadFromSession<string>('selectedStartTime'),
          loadFromSession<string>('selectedEndTime'),
          loadFromSession<number>('selectedBayId'),
          loadFromSession<User>('guestUser'),
          loadFromSession<boolean>('isExistingUser'),
          loadFromSession<number>('guestCount'),
        ]);
        
        if (storedDate) {
          // If stored value is already YYYY-MM-DD, use it directly.
          // Old sessions stored a full ISO timestamp — discard those to avoid
          // the UTC-offset shift that extractLocalDateISO(new Date(isoString)) causes.
          if (/^\d{4}-\d{2}-\d{2}$/.test(storedDate)) {
            setSelectedDateState(asDateString(storedDate));
          }
        }
        // Times get the same treatment as the date above: session storage is
        // user-writable, and these values flow straight into
        // `createClubDateTimeFromSlot` and `formatTime`, both of which now reject
        // malformed input rather than producing a wrong instant or rendering
        // "NaN:NaN AM". Discarding a bad value drops the user back to picking a
        // time; a throw mid-render would take the whole booking flow down.
        if (storedStartTime && isValidSlotTime(storedStartTime)) {
          setSelectedStartTimeState(storedStartTime);
        }
        if (storedEndTime && isValidSlotTime(storedEndTime)) {
          setSelectedEndTimeState(storedEndTime);
        }
        if (storedBayId !== null) {
          setSelectedBayIdState(storedBayId);
        }
        if (storedGuestUser) {
          setGuestUserState(storedGuestUser);
        }
        if (storedIsExistingUser !== null) {
          setIsExistingUserState(storedIsExistingUser);
        }
        if (storedGuestCount !== null && storedGuestCount >= 0) {
          setGuestCountState(storedGuestCount);
        }
        // Note: paymentMethodToken is NOT persisted for security reasons
      } catch (error) {
        if (import.meta.env.DEV) {
          console.warn('[BookingContext] Failed to load initial state from storage', error);
        }
      } finally {
        setIsInitialized(true);
      }
    }
    
    loadInitialState();
  }, [persist]);
  
  // Persist to storage on changes (only after initial load)
  useEffect(() => {
    if (!isInitialized || !persist) return;
    saveToSession('selectedDate', selectedDate);
  }, [selectedDate, isInitialized, persist]);
  
  useEffect(() => {
    if (!isInitialized || !persist) return;
    if (selectedStartTime) {
      saveToSession('selectedStartTime', selectedStartTime);
    } else {
      clearFromSession('selectedStartTime');
    }
  }, [selectedStartTime, isInitialized, persist]);
  
  useEffect(() => {
    if (!isInitialized || !persist) return;
    if (selectedEndTime) {
      saveToSession('selectedEndTime', selectedEndTime);
    } else {
      clearFromSession('selectedEndTime');
    }
  }, [selectedEndTime, isInitialized, persist]);
  
  useEffect(() => {
    if (!isInitialized || !persist) return;
    if (selectedBayId !== null) {
      saveToSession('selectedBayId', selectedBayId);
    } else {
      clearFromSession('selectedBayId');
    }
  }, [selectedBayId, isInitialized, persist]);
  
  // Persist guest user data (needed across page refreshes)
  useEffect(() => {
    if (!isInitialized || !persist) return;
    if (guestUser) {
      saveToSession('guestUser', guestUser);
    } else {
      clearFromSession('guestUser');
    }
  }, [guestUser, isInitialized, persist]);
  
  // Persist isExistingUser flag
  useEffect(() => {
    if (!isInitialized || !persist) return;
    saveToSession('isExistingUser', isExistingUser);
  }, [isExistingUser, isInitialized, persist]);

  // Persist guest count
  useEffect(() => {
    if (!isInitialized || !persist) return;
    saveToSession('guestCount', guestCount);
  }, [guestCount, isInitialized, persist]);
  
  /**
   * Clear all booking selections
   * 
   * Resets to default state and clears sessionStorage.
   */
  const clearBooking = useCallback(() => {
    setSelectedDateState(extractLocalDateISO(new Date()));
    setSelectedStartTimeState(null);
    setSelectedEndTimeState(null);
    setSelectedBayIdState(null);
    setGuestCountState(0);
    setGuestUserState(null);
    setIsExistingUserState(false);
    setPaymentMethodTokenState(null);
    setPaymentProviderState(null);
    setPayAtClubState(false);
    setAvailableBaysState(null);
    setWaiverSignaturesState({});
    setWaiverAgreementsState({});
    clearAllBookingData();
  }, []);
  
  // Listen for clear booking events (e.g., from MainLayout home button)
  useEffect(() => {
    const handleClearBooking = () => {
      if (import.meta.env.DEV) {
        console.log('[BookingContext] Received clear booking event');
      }
      clearBooking();
    };
    
    window.addEventListener('butter:clearBooking', handleClearBooking);
    return () => window.removeEventListener('butter:clearBooking', handleClearBooking);
  }, [clearBooking]);
  
  /**
   * Update selected date
   * 
   * Clears time selections when date changes (times are date-specific)
   */
  const setSelectedDate = useCallback((date: DateString) => {
    setSelectedDateState(date);
    // Clear times when date changes (times are date-specific)
    setSelectedStartTimeState(null);
    setSelectedEndTimeState(null);
  }, []);
  
  /**
   * Update selected start time
   */
  const setSelectedStartTime = useCallback((time: string | null) => {
    setSelectedStartTimeState(time);
  }, []);
  
  /**
   * Update selected end time
   */
  const setSelectedEndTime = useCallback((time: string | null) => {
    setSelectedEndTimeState(time);
  }, []);
  
  /**
   * Update selected bay ID
   */
  const setSelectedBayId = useCallback((bayId: number | null) => {
    setSelectedBayIdState(bayId);
  }, []);

  /**
   * Update guest count (0 = solo booking, no guests)
   */
  const setGuestCount = useCallback((count: number) => {
    setGuestCountState(Math.max(0, count));
  }, []);
  
  /**
   * Set guest user data
   * 
   * @param user - Guest user object from /users/guest endpoint
   * @param isExisting - Flag indicating if user already existed
   */
  const setGuestUser = useCallback((user: User | null, isExisting: boolean) => {
    setGuestUserState(user);
    setIsExistingUserState(isExisting);
  }, []);
  
  /**
   * Set payment method token
   * 
   * NOTE: Token is NOT persisted to storage for security.
   * It will be cleared on page refresh.
   * 
   * @param token - Payment token from Stripe/Square
   * @param provider - Payment provider used
   */
  const setPaymentMethodToken = useCallback((token: string | null, provider: PaymentProvider | null) => {
    setPaymentMethodTokenState(token);
    setPaymentProviderState(provider);
  }, []);

  /** A guest continued without a card — the club collects at the counter. */
  const setPayAtClub = useCallback((value: boolean) => {
    setPayAtClubState(value);
  }, []);
  
  /**
   * Set available bays
   * 
   * @param bays - Availability payload containing available bay items
   */
  const setAvailableBays = useCallback((bays: DurationBayAvailabilityPayload | null) => {
    setAvailableBaysState(bays);
  }, []);
  
  /**
   * Clear payment and user data
   * 
   * Clears guest user, payment token, and related flags.
   * Use this after booking completion or when user cancels.
   */
  const clearPaymentData = useCallback(() => {
    setGuestUserState(null);
    setIsExistingUserState(false);
    setPaymentMethodTokenState(null);
    setPaymentProviderState(null);
    setPayAtClubState(false);
    clearFromSession('guestUser');
    clearFromSession('isExistingUser');
  }, []);
  
  /**
   * Record a drawn signature for one waiver version.
   *
   * Keyed by version id, not waiver id — a member signs a specific revision, and
   * if the club publishes an update mid-flow the old signature must not silently
   * satisfy the new one.
   */
  const setWaiverSignature = useCallback(
    (waiverVersionId: number, signature: WaiverSignaturePaths | null) => {
      setWaiverSignaturesState((previous) => ({ ...previous, [waiverVersionId]: signature }));
    },
    [],
  );

  const setWaiverAgreement = useCallback((waiverVersionId: number, agreed: boolean) => {
    setWaiverAgreementsState((previous) => ({ ...previous, [waiverVersionId]: agreed }));
  }, []);

  /** Clear captured signatures. Called after the booking completes. */
  const clearWaiverData = useCallback(() => {
    setWaiverSignaturesState({});
    setWaiverAgreementsState({});
  }, []);

  /**
   * Check if booking has all required selections
   *
   * @returns true if date, start time, end time, and bay are all selected
   */
  const isBookingValid = useCallback((): boolean => {
    return !!(
      selectedDate && 
      selectedStartTime && 
      selectedEndTime && 
      selectedBayId !== null
    );
  }, [selectedDate, selectedStartTime, selectedEndTime, selectedBayId]);
  
  // Context value
  const value: BookingContextValue = {
    // Booking Selection State
    selectedDate,
    selectedStartTime,
    selectedEndTime,
    selectedBayId,
    guestCount,
    
    // Payment & User State
    guestUser,
    paymentMethodToken,
    paymentProvider,
    payAtClub,
    isExistingUser,
    availableBays,

    // Waiver State
    waiverSignatures,
    waiverAgreements,

    // Booking Selection Actions
    setSelectedDate,
    setSelectedStartTime,
    setSelectedEndTime,
    setSelectedBayId,
    setGuestCount,
    
    // Payment & User Actions
    setGuestUser,
    setPaymentMethodToken,
    setPayAtClub,
    setAvailableBays,
    clearPaymentData,
    clearBooking,

    // Waiver Actions
    setWaiverSignature,
    setWaiverAgreement,
    clearWaiverData,

    // Validation
    isBookingValid,
  };
  
  return (
    <BookingContext.Provider value={value}>
      {children}
    </BookingContext.Provider>
  );
}
